import React, { useState, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { apiFetch } from './api';

const TaskPopup = ({ taskId, onClose }) => {
  const [name, setName] = useState('');
  const [status, setStatus] = useState('offen');
  const [prioritaet, setPrioritaet] = useState('mittel');
  const [von, setVon] = useState('');
  const [bis, setBis] = useState('');
  const [deadline, setDeadline] = useState('');
  const [erfasstMin, setErfasstMin] = useState(0);
  const [loading, setLoading] = useState(true);
  const [gespeichert, setGespeichert] = useState(false);
  const [alleUsers, setAlleUsers] = useState([]);
  const [members, setMembers] = useState([]);
  const [mutterFarbe, setMutterFarbe] = useState(null);
  const [mutterName, setMutterName] = useState('');
  const [projektName, setProjektName] = useState('');

  const nameRef = useRef('');
  const statusRef = useRef('offen');
  const prioRef = useRef('mittel');
  const vonRef = useRef('');
  const bisRef = useRef('');
  const deadlineRef = useRef('');
  useEffect(() => { nameRef.current = name; }, [name]);
  useEffect(() => { statusRef.current = status; }, [status]);
  useEffect(() => { prioRef.current = prioritaet; }, [prioritaet]);
  useEffect(() => { vonRef.current = von; }, [von]);
  useEffect(() => { bisRef.current = bis; }, [bis]);
  useEffect(() => { deadlineRef.current = deadline; }, [deadline]);

  const editor = useEditor({
    extensions: [StarterKit, Underline],
    content: '',
    onUpdate: ({ editor }) => { speichern({ notizen: editor.getHTML() }); }
  });

  const speichern = async ({ notizen } = {}) => {
    await apiFetch(`/tasks/${taskId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: nameRef.current,
        status: statusRef.current,
        prioritaet: prioRef.current,
        von: vonRef.current || null,
        bis: bisRef.current || null,
        deadline: deadlineRef.current || null,
        beschreibung: '',
        notizen: notizen !== undefined ? notizen : (editor ? editor.getHTML() : '')
      })
    });
    setGespeichert(true);
    setTimeout(() => setGespeichert(false), 2000);
  };

  const ladeMembers = async () => {
    const res = await apiFetch(`/tasks/${taskId}`);
    const data = await res.json();
    setMembers(data.members || []);
    setErfasstMin(data.erfasste_minuten || 0);
  };

  useEffect(() => {
    const ladeAlles = async () => {
      const [taskRes, usersRes] = await Promise.all([
        apiFetch(`/tasks/${taskId}`),
        apiFetch('/users')
      ]);
      const data = await taskRes.json();
      const users = await usersRes.json();
      setName(data.name);
      setStatus(data.status || 'offen');
      setPrioritaet(data.prioritaet || 'mittel');
      setVon(data.von || '');
      setBis(data.bis || '');
      setDeadline(data.deadline || '');
      setErfasstMin(data.erfasste_minuten || 0);
      setMutterFarbe(data.mutter_farbe || null);
      setMutterName(data.mutter_name || '');
      setProjektName(data.projekt_name || '');
      nameRef.current = data.name;
      statusRef.current = data.status || 'offen';
      prioRef.current = data.prioritaet || 'mittel';
      vonRef.current = data.von || '';
      bisRef.current = data.bis || '';
      deadlineRef.current = data.deadline || '';
      setMembers(data.members || []);
      setAlleUsers(users);
      setLoading(false);
      if (editor) editor.commands.setContent(data.notizen || '');
    };
    if (taskId) ladeAlles();
  }, [taskId, editor]);

  const personHinzufuegen = async (userId) => {
    await apiFetch(`/tasks/${taskId}/members`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId })
    });
    ladeMembers();
  };

  const personEntfernen = async (userId) => {
    await apiFetch(`/tasks/${taskId}/members/${userId}`, { method: 'DELETE' });
    ladeMembers();
  };

  const taskLoeschen = async () => {
    await apiFetch(`/tasks/${taskId}`, { method: 'DELETE' });
    onClose();
  };

  const feldAendern = async (setter, ref, wert, extraSetter, extraRef, extraCheck) => {
    setter(wert);
    ref.current = wert;
    if (extraSetter && extraRef && !extraCheck) {
      extraSetter(wert);
      extraRef.current = wert;
    }
    await speichern();
  };

  const formatZeit = (minuten) => {
    if (!minuten || minuten === 0) return '0h';
    const h = Math.floor(minuten / 60);
    const m = minuten % 60;
    if (h === 0) return `${m}min`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}min`;
  };

  const zugewieseneIds = members.map(m => m.id);
  const verfuegbar = alleUsers.filter(u => !zugewieseneIds.includes(u.id));

  const btn = (a) => ({
    minWidth: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '0 8px', backgroundColor: a ? '#f0f0ee' : 'transparent', color: a ? '#1a1a1a' : '#6b6b6b',
    border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer'
  });
  const divider = <div style={{ width: '1px', backgroundColor: '#ececec', margin: '5px 3px' }} />;

  const chip = (m) => (
    <div key={m.id} style={{
      display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: '#f7f7f5',
      borderRadius: '20px', padding: '5px 5px 5px 12px', fontSize: '13px'
    }}>
      <div style={{
        width: '22px', height: '22px', borderRadius: '50%', backgroundColor: '#e4e4e1',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '600', color: '#6b6b6b'
      }}>{m.name.charAt(0).toUpperCase()}</div>
      <span style={{ color: '#1a1a1a' }}>{m.name}</span>
      <button onClick={() => personEntfernen(m.id)} style={{
        width: '20px', height: '20px', borderRadius: '50%', border: 'none',
        backgroundColor: 'transparent', color: '#9b9b9b', cursor: 'pointer', fontSize: '14px'
      }}>×</button>
    </div>
  );

  const sektionLabel = (t) => (
    <p style={{ fontSize: '11px', fontWeight: '500', color: '#9b9b9b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>{t}</p>
  );

  const selectStyle = {
    width: '100%', padding: '8px 12px', fontSize: '13px', border: '1px solid #e8e8e8',
    borderRadius: '8px', outline: 'none', cursor: 'pointer', backgroundColor: '#ffffff', color: '#1a1a1a'
  };
  const dateStyle = { ...selectStyle, cursor: 'text' };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.35)', display: 'flex',
      alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000,
      padding: '60px 20px', overflowY: 'auto'
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        backgroundColor: '#ffffff', borderRadius: '14px', width: '100%', maxWidth: '760px',
        boxShadow: '0 12px 48px rgba(0,0,0,0.18)', position: 'relative', overflow: 'hidden'
      }}>
        {mutterFarbe && (
          <div style={{ height: '4px', backgroundColor: mutterFarbe, width: '100%' }} />
        )}

        <div style={{ padding: '40px' }}>
          {gespeichert && (
            <div style={{
              position: 'absolute', top: '20px', right: '52px', fontSize: '12px', color: '#6b6b6b',
              backgroundColor: '#f7f7f5', borderRadius: '8px', padding: '5px 10px'
            }}>✓ Gespeichert</div>
          )}

          <button onClick={onClose} style={{
            position: 'absolute', top: '20px', right: '20px', width: '28px', height: '28px',
            borderRadius: '6px', border: 'none', backgroundColor: '#f0f0ee', color: '#6b6b6b',
            cursor: 'pointer', fontSize: '16px'
          }}>×</button>

          {loading ? (
            <p style={{ color: '#9b9b9b', fontSize: '14px' }}>Lädt...</p>
          ) : (
            <>
              {(mutterName || projektName) && (
                <p style={{
                  fontSize: '11px', fontWeight: '500', color: mutterFarbe || '#9b9b9b',
                  textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px'
                }}>
                  {mutterName}{mutterName && projektName ? ' · ' : ''}{projektName}
                </p>
              )}

              <input type="text" value={name} onChange={(e) => setName(e.target.value)} onBlur={() => speichern()}
                style={{
                  width: '100%', fontSize: '24px', fontWeight: '700', color: '#1a1a1a', border: 'none',
                  outline: 'none', backgroundColor: 'transparent', marginBottom: '24px', letterSpacing: '-0.4px', boxSizing: 'border-box'
                }} />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                <div>
                  {sektionLabel('Status')}
                  <select value={status} onChange={(e) => feldAendern(setStatus, statusRef, e.target.value)} style={selectStyle}>
                    <option value="offen">Offen</option>
                    <option value="in arbeit">In Arbeit</option>
                    <option value="erledigt">Erledigt</option>
                  </select>
                </div>
                <div>
                  {sektionLabel('Priorität')}
                  <select value={prioritaet} onChange={(e) => feldAendern(setPrioritaet, prioRef, e.target.value)} style={selectStyle}>
                    <option value="niedrig">Niedrig</option>
                    <option value="mittel">Mittel</option>
                    <option value="hoch">Hoch</option>
                  </select>
                </div>
                <div>
                  {sektionLabel('Deadline')}
                  <input type="date" value={deadline || ''} onChange={(e) => feldAendern(setDeadline, deadlineRef, e.target.value)} style={dateStyle} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px', marginBottom: '20px' }}>
                <div>
                  {sektionLabel('Von (geplant)')}
                  <input type="date" value={von || ''} onChange={(e) => feldAendern(setVon, vonRef, e.target.value, setBis, bisRef, bis)} style={dateStyle} />
                </div>
                <div>
                  {sektionLabel('Bis (geplant)')}
                  <input type="date" value={bis || ''} onChange={(e) => feldAendern(setBis, bisRef, e.target.value, setVon, vonRef, von)} style={dateStyle} />
                </div>
                <div />
              </div>

              <div style={{ display: 'flex', gap: '32px', marginBottom: '24px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '240px' }}>
                  {sektionLabel('Zugewiesen an')}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {members.map(chip)}
                    <select value="" disabled={verfuegbar.length === 0}
                      onChange={(e) => { if (e.target.value) personHinzufuegen(Number(e.target.value)); }}
                      style={{
                        padding: '6px 12px', fontSize: '13px', border: '1px dashed #d4d4d4', borderRadius: '20px',
                        outline: 'none', cursor: 'pointer', color: '#6b6b6b', backgroundColor: 'transparent',
                        opacity: verfuegbar.length === 0 ? 0.4 : 1
                      }}>
                      <option value="">+ Person</option>
                      {verfuegbar.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  {sektionLabel('Erfasste Zeit')}
                  <div style={{
                    fontSize: '20px', fontWeight: '700', color: '#1a1a1a', letterSpacing: '-0.5px', padding: '6px 0'
                  }}>{formatZeit(erfasstMin)}</div>
                </div>
              </div>

              <div style={{ borderTop: '1px solid #efefef', marginBottom: '20px' }} />
              {sektionLabel('Notizen')}

              <div style={{ fontSize: '14px', color: '#1a1a1a', lineHeight: '1.7', minHeight: '180px' }}>
                <style>{`
                  .ProseMirror { outline: none; min-height: 180px; }
                  .ProseMirror p { margin: 0 0 8px 0; }
                  .ProseMirror p.is-editor-empty:first-child::before { content: 'Notizen schreiben...'; color: #c4c4c4; pointer-events: none; float: left; height: 0; }
                  .ProseMirror strong { font-weight: 600; }
                  .ProseMirror em { font-style: italic; }
                  .ProseMirror u { text-decoration: underline; }
                  .ProseMirror h1 { font-size: 22px; font-weight: 700; margin: 16px 0 8px 0; }
                  .ProseMirror h2 { font-size: 18px; font-weight: 600; margin: 12px 0 6px 0; }
                  .ProseMirror h3 { font-size: 15px; font-weight: 600; margin: 10px 0 4px 0; }
                  .ProseMirror ul, .ProseMirror ol { padding-left: 22px; margin: 8px 0; }
                  .ProseMirror li { margin-bottom: 4px; }
                `}</style>
                {editor && (
                  <BubbleMenu editor={editor}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '1px', backgroundColor: '#ffffff',
                      border: '1px solid #ececec', borderRadius: '10px', padding: '4px',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.10), 0 1px 3px rgba(0,0,0,0.06)'
                    }}>
                      <button onClick={() => editor.chain().focus().toggleBold().run()} style={btn(editor.isActive('bold'))}><strong>B</strong></button>
                      <button onClick={() => editor.chain().focus().toggleItalic().run()} style={btn(editor.isActive('italic'))}><em style={{ fontFamily: 'Georgia, serif' }}>i</em></button>
                      <button onClick={() => editor.chain().focus().toggleUnderline().run()} style={btn(editor.isActive('underline'))}><span style={{ textDecoration: 'underline' }}>U</span></button>
                      <button onClick={() => editor.chain().focus().toggleStrike().run()} style={btn(editor.isActive('strike'))}><s>S</s></button>
                      {divider}
                      <button onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} style={btn(editor.isActive('heading', { level: 1 }))}>H1</button>
                      <button onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} style={btn(editor.isActive('heading', { level: 2 }))}>H2</button>
                      <button onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} style={btn(editor.isActive('heading', { level: 3 }))}>H3</button>
                      {divider}
                      <button onClick={() => editor.chain().focus().toggleBulletList().run()} style={btn(editor.isActive('bulletList'))}>•</button>
                      <button onClick={() => editor.chain().focus().toggleOrderedList().run()} style={btn(editor.isActive('orderedList'))}>1.</button>
                    </div>
                  </BubbleMenu>
                )}
                <EditorContent editor={editor} />
              </div>

              <div style={{ borderTop: '1px solid #efefef', marginTop: '24px', paddingTop: '16px' }}>
                <button onClick={taskLoeschen} style={{
                  padding: '6px 14px', backgroundColor: 'transparent', border: '1px solid #ffdddd',
                  borderRadius: '6px', fontSize: '12px', color: '#cc0000', cursor: 'pointer'
                }}>Task löschen</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskPopup;